import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faCheckCircle,
  faTimes,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { apiSearchBanks } from "../../../../utils/queries";
import "./BankDetails.css";

const searchTypeOptions = [
  { value: "name", label: "Bank name" },
  { value: "bic", label: "BIC / SWIFT" },
  { value: "routingNumber", label: "Routing number" },
  { value: "country", label: "Country" },
];

const placeholders = {
  name: "e.g., JPMORGAN CHASE",
  bic: "e.g., CHASUS33",
  routingNumber: "e.g., 021000021",
};

const BankSearch = ({ token, onBankSelect, selectedBank, countryList }) => {
  const [searchType, setSearchType] = useState("name");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounce search
  useEffect(() => {
    // For country search, only search when a country is selected
    if (searchType === "country") {
      if (!selectedCountry) {
        setSearchResults([]);
        setHasSearched(false);
        return;
      }
      performSearch();
      return;
    }

    // For text-based searches, debounce the input
    if (searchTerm.length < 2) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    const delayDebounce = setTimeout(() => {
      performSearch();
    }, 500);

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, searchType, selectedCountry]);

  const performSearch = async () => {
    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const searchValue = searchType === "country" ? selectedCountry : searchTerm;
      const response = await apiSearchBanks(searchType, searchValue, token);
      const banks = response.data || [];

      const activeBanks = banks.filter((bank) => bank.effectiveStatus === "A");
      setSearchResults(activeBanks);

      if (activeBanks.length === 0) {
        setError(
          "No banks found in MyHR for that search. If your bank isn't listed, you can enter the bank details manually in Option B below."
        );
      }
    } catch (err) {
      let errorMessage = "Failed to search banks. ";
      if (err.response) {
        errorMessage += `Error ${err.response.status}: ${err.response.statusText || "Server error"}`;
      } else if (err.request) {
        errorMessage += "The server did not respond. Please try again or enter your bank details manually in Option B below.";
      } else {
        errorMessage += err.message || "Please try again later.";
      }
      setError(errorMessage);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankSelect = (bank) => {
    onBankSelect(bank);
    setSearchTerm("");
    setSelectedCountry(null);
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleTabChange = (type) => {
    setSearchType(type);
    setSearchTerm("");
    setSelectedCountry(null);
    setSearchResults([]);
    setHasSearched(false);
    setError(null);
  };

  const countryOptions = (countryList || []).map((country) => ({
    label: country.countryName,
    value: country.countryCodeAlpha3,
    id: country.id,
  }));

  // If a bank is already selected, show the selected state
  if (selectedBank) {
    return (
      <div className="bs-selected">
        <FontAwesomeIcon icon={faCheckCircle} className="bs-selected__icon" />
        <div className="bs-selected__body">
          <div className="bs-selected__name">{selectedBank.bankName}</div>
          <div className="bs-selected__meta">
            {selectedBank.bankCountry && (
              <span className="bs-selected__chip">{selectedBank.bankCountry}</span>
            )}
            {selectedBank.businessIdentifierCode && (
              <span className="bs-selected__chip">
                BIC {selectedBank.businessIdentifierCode}
              </span>
            )}
            {selectedBank.routingNumber && (
              <span className="bs-selected__chip">
                Routing {selectedBank.routingNumber}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="bs-selected__clear"
          onClick={() => onBankSelect(null)}
          aria-label="Clear selected bank"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    );
  }

  return (
    <div className="bs-container">
      {/* Search type tabs */}
      <div className="bs-tabs">
        {searchTypeOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`bs-tab${searchType === option.value ? " bs-tab--active" : ""}`}
            onClick={() => handleTabChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Search input */}
      {searchType === "country" ? (
        <Select
          className="bs-country-select"
          classNamePrefix="bs-country-select"
          value={countryOptions.find((opt) => opt.value === selectedCountry) || null}
          onChange={(option) => setSelectedCountry(option?.value || null)}
          options={countryOptions}
          placeholder="Select a country..."
          isClearable
        />
      ) : (
        <div className="bs-input-wrap">
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : faSearch}
            className={`bs-input-icon${isLoading ? " bs-input-icon--spin" : ""}`}
          />
          <input
            type="text"
            className="bs-input"
            placeholder={placeholders[searchType]}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {isLoading && <div className="bs-loading">Searching banks...</div>}

      {error && !isLoading && (
        <div className="bs-notice bs-notice--warn">
          <span>{error}</span>
          <button
            type="button"
            className="bs-retry-btn"
            onClick={performSearch}
          >
            <FontAwesomeIcon icon={faRotateRight} /> Retry
          </button>
        </div>
      )}

      {!isLoading && searchResults.length > 0 && (
        <div className="bs-results">
          <div className="bs-results__count">
            {searchResults.length} bank{searchResults.length !== 1 ? "s" : ""} found
          </div>
          {searchResults.slice(0, 10).map((bank, index) => (
            <div
              key={index}
              className="bs-result-row"
              onClick={() => handleBankSelect(bank)}
            >
              <div className="bs-result-row__body">
                <div className="bs-result-row__name">{bank.bankName}</div>
                <div className="bs-result-row__meta">
                  {bank.bankCountry && (
                    <span className="bs-result-row__chip">{bank.bankCountry}</span>
                  )}
                  {bank.businessIdentifierCode && (
                    <span className="bs-result-row__chip">
                      BIC {bank.businessIdentifierCode}
                    </span>
                  )}
                  {bank.routingNumber && (
                    <span className="bs-result-row__chip">
                      Routing {bank.routingNumber}
                    </span>
                  )}
                  {bank.city && (
                    <span className="bs-result-row__chip">{bank.city}</span>
                  )}
                </div>
              </div>
              <span className="bs-result-row__select">Select</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BankSearch;
